// Дела: очередь того, что требует внимания.
//
// ## Почему дела собираются из СУЩЕСТВУЮЩИХ выборок, а не своим запросом
//
// Каждое дело ведёт в чужой раздел, и число в деле обязано совпасть с числом строк, которые
// человек там увидит. Свой серверный счётчик — это вторая реализация того же правила
// видимости; разойдутся они молча, и «в деле пять, в списке две» человек прочитает как
// поломку системы. Поэтому дело считается ровно тем запросом, в который оно ведёт.
//
// Цена решения названа честно: список заявок выгружается целиком ради двух чисел. На объёме
// заказчика (74 заявки в очереди за месяц) это дешевле, чем расхождение; когда очередь
// вырастет, сервер отдаст счётчик — и дело переедет на него, не меняя экрана.
//
// ## Дела не «показывают всё», а перечисляют то, что ждёт решения
//
// Дела с нулём строк на экране НЕ показываются: очередь, в которой девять пунктов и во всех
// ноль, читается как «всё сломано» или перестаёт читаться вовсе. Ноль — это спокойствие, и
// его место в общей фразе внизу, а не строкой.

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { fetchClaims, fetchPersons } from '@/shared/api/endpoints/accountable';
import type {
  IncompleteRateListOut,
  OnShiftUnassignedListOut,
} from '@/shared/api/endpoints/people';
import {
  fetchIncompleteRate,
  fetchOnShiftUnassigned,
  listEmployees,
} from '@/shared/api/endpoints/people';
import { countLabel } from '@/shared/text/plural';
import { fetchClosePreview, fetchPeriods } from '@/shared/api/endpoints/official';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import type { Period } from '@/shared/time/period';
import { formatPeriodTitle, расчётныйМесяц } from '@/shared/time/period';

import { ПОРОГ_ЗАВИСАНИЯ_ДНЕЙ } from './route';

/** «1 день», «2 дня», «5 дней». Формы те же, что у сторожа на сервере. */
const ДНИ = (сколько: number): string => countLabel(сколько, ['день', 'дня', 'дней']);

/** Одно дело: что ждёт, сколько строк, на какую сумму и куда идти. */
export interface Дело {
  код: string;
  заголовок: string;
  /** Почему это дело: причина словами, без кодов и жаргона. */
  пояснение: string;
  сколько: number;
  /**
   * Что именно считает число: «человек», «заявок», «траншей».
   *
   * Без слова число в строке ничего не значит: «267» рядом с суммой человек читает как ещё
   * одну сумму. Раньше слово стояло в пояснении («у 267 человек остаток не погашен») — то
   * есть то же число писалось на экране дважды.
   */
  единица: [string, string, string];
  /** Копейки. `null` — у дела нет денежного веса (например, недобор по людям). */
  сумма: number | null;
  адрес: string;
  /** Срочность: по ней дела сортируются, а не по алфавиту. */
  вес: number;
}

export interface ДелаСводка {
  дела: Дело[];
  /** Момент среза: показывается в шапке, как и в списках. */
  generated_at: string;
  /**
   * Список «на вахте без поста» ПРАВДА ответил. Источник мягкий к отказу: упал или старый
   * сервер ответил «нет такого адреса» — дела этого вида просто нет. Но тогда и пустая очередь
   * не вправе писать «все люди на вахте стоят на постах»: этого никто не проверил.
   */
  безПостаПроверен: boolean;
}

/**
 * Какие источники очереди человеку доступны.
 *
 * Заведено 02.09.2026 прогоном девяти ролей. До него `собрать()` ходила во все четыре
 * выборки безусловно, и первый же отказ рушил ВЕСЬ экран: заместитель по охране, табельщик,
 * рекрутёр и старший объекта встречали «Дела» красной надписью «не удалось загрузить». У
 * рекрутёра это был первый экран после входа — раздел стоял у него первым в полосе.
 *
 * Отсутствие права здесь не ошибка, а норма: у табельщика нет подотчёта, у заместителя по
 * охране он скрыт по тз. Значит источник, до которого человек не дотягивается, просто не
 * спрашивается, а очередь собирается из остальных.
 */
export interface ИсточникиДел {
  подотчёт: boolean;
  люди: boolean;
  контур: boolean;
  тарифы: boolean;
  /**
   * «На вахте, но не на посту». Нужны ОБА права: правка людей (список открыт только тем, кто
   * может продлить, — иначе он дал бы тревогу без выхода) и табель (дело ведёт в панель поверх
   * табеля, и без табеля человек пришёл бы в раздел, которого не видит).
   */
  безПоста: boolean;
}

export function источникиПоПравам(permissions: readonly string[]): ИсточникиДел {
  return {
    подотчёт: can(permissions, 'core.accountable.view'),
    люди: can(permissions, 'core.employee.view'),
    контур: can(permissions, 'core.official.view'),
    тарифы: can(permissions, 'core.rate.view'),
    безПоста: can(permissions, 'core.employee.edit') && can(permissions, 'core.timesheet.view'),
  };
}

/**
 * Дело «не заполнен тариф» — по тому же рабочему листу, в который оно ведёт.
 *
 * Проход по боевым данным 23.09.2026: у заказчика 316 назначений без тарифа, ведомости за июль
 * посчитаны у 21 объекта из 76, а «Дела» писали «1 дело ждёт решения» — про подотчёт одного
 * человека. Без тарифа ведомость человека не считается вовсе, и утвердить месяц нельзя: это
 * главное, что мешает выдавать зарплату, и молчать о нём очередь дел не имеет права.
 *
 * Число — людей, а не назначений: у человека бывает два поста, и «316» против «154 человек»
 * в листе читалось бы как расхождение. Лист называет оба числа своей строкой сверху.
 */
export function делоТарифа(лист: IncompleteRateListOut, месяц: Period): Дело | null {
  if (лист.employees === 0) {
    return null;
  }
  const вАдресе = `${месяц.year}-${String(месяц.month).padStart(2, '0')}`;
  const один = лист.employees === 1;
  return {
    код: 'rate_missing',
    заголовок: 'Не заполнен тариф',
    пояснение: `${formatPeriodTitle(месяц)}: начислять ${один ? 'этому человеку' : 'этим людям'} не из чего — нет ни суммы за период с нормой часов, ни ставки. Ведомость ${один ? 'его' : 'их'} не посчитает, и утвердить месяц не получится.`,
    сколько: лист.employees,
    единица: ['человек', 'человека', 'человек'],
    // Суммы у дела нет: её и не из чего посчитать, в этом всё дело.
    сумма: null,
    адрес: `/payroll/sheets?f.period=${вАдресе}&p=sheet-tariff:${вАдресе}`,
    // Выше долгов и траншей: без тарифа месяц не закрыть вовсе, а зарплату ждут все.
    вес: 190,
  };
}

/**
 * Дело «На вахте, но не на посту» — по тому же списку, в который оно ведёт.
 *
 * 10.10.2026 заказчик понял дату «По» как «период работы» и закрыл назначения сентябрём. Замер
 * боя 19.10.2026: тридцать человек «на вахте» без действующего и будущего назначения, — их нет ни
 * в табеле, ни в ведомости октября, и ни один экран об этом не говорил. Человек продолжает
 * работать, а денег за октябрь ему не посчитают.
 *
 * Число — люди: у каждого в списке одно, последнее, назначение. Ведёт в панель поверх табеля, где
 * их продлевают пачкой; ничего там не отмечено заранее — среди тридцати бывают и правда уехавшие.
 */
export function делоБезПоста(список: OnShiftUnassignedListOut): Дело | null {
  if (список.items.length === 0) {
    return null;
  }
  return {
    код: 'on_shift_unassigned',
    заголовок: 'На вахте, но не на посту',
    пояснение:
      'Назначение кончилось или его не было, а в карточке «на вахте»: в табель и ведомость их не возьмут, пока назначение не продлят или человека не поставят на пост.',
    сколько: список.items.length,
    единица: ['человек', 'человека', 'человек'],
    // Суммы нет: за непосчитанный месяц её не из чего взять — в этом и беда.
    сумма: null,
    // Панель табеля `ts-unassigned` с опознавателем «все объекты» (`modules/payroll/route.ts`).
    // Адрес буквально, как у дела тарифа: «Дела» не ходят за кодом в чужой раздел.
    адрес: '/payroll/timesheet?p=ts-unassigned:all',
    // Между тарифом (190) и зависшими деньгами (200): без назначения человека нет ни в табеле,
    // ни в ведомости, то есть не посчитают не тариф, а его целиком.
    вес: 195,
  };
}

/**
 * Собрать очередь из источников, доступных человеку.
 *
 * Экспортируется ради проверки устойчивости: источник, которого нет на старом сервере, не
 * имеет права уронить остальные дела.
 */
export async function собрать(источники: ИсточникиДел): Promise<ДелаСводка> {
  const дела: Дело[] = [];

  // Источники независимы, поэтому идут ОДНОВРЕМЕННО. Последовательные запросы стоили бы
  // человеку суммы их задержек: на связи до базы разработки это десять секунд скелета вместо
  // трёх, а «Дела» открывают первыми, ещё не сняв куртку.
  //
  // `null` вместо ответа означает «этот источник человеку не положен», а не «пусто»: дело по
  // нему не появится, и это верно — оно и не его.
  const месяц = расчётныйМесяц(new Date());
  const [заявки, лица, периоды, уволенные, безТарифа, безПоста] = await Promise.all([
    источники.подотчёт ? fetchClaims({ status: ['submitted'] }) : null,
    источники.подотчёт ? fetchPersons({ active_only: true }) : null,
    источники.контур ? fetchPeriods({}) : null,
    // Тот же запрос, в который ведёт дело: своим счётчиком число разошлось бы со списком.
    источники.люди ? listEmployees({ status: 'fired', has_debt: true, limit: 500 }) : null,
    // И здесь тот же: рабочий лист тарифов за расчётный месяц.
    источники.тарифы ? fetchIncompleteRate({ year: месяц.year, month: месяц.month }) : null,
    // Тот же список, что открывает панель «Не попали в табель». МЯГКИЙ к отказу, в отличие от
    // остальных: маршрут появился 19.10.2026, а кабинет зашит в установщик и обновляется не
    // вместе с сервером. Новый кабинет на старом сервере получил бы здесь «нет такого адреса»,
    // и `Promise.all` уронил бы весь экран дел вместе с чеками и долгами. Отказ — значит дела
    // этого вида просто нет, остальные живут.
    источники.безПоста ? fetchOnShiftUnassigned().catch(() => null) : null,
  ]);

  // ── Не заполнен тариф ────────────────────────────────────────────────────────────────
  const тариф = безТарифа === null ? null : делоТарифа(безТарифа, месяц);
  if (тариф !== null) {
    дела.push(тариф);
  }

  // ── На вахте, но не на посту ─────────────────────────────────────────────────────────
  const безНазначения = безПоста === null ? null : делоБезПоста(безПоста);
  if (безНазначения !== null) {
    дела.push(безНазначения);
  }

  // ── Чеки ждут приёмки ────────────────────────────────────────────────────────────────
  if (заявки !== null && заявки.items.length > 0) {
    дела.push({
      код: 'claims_submitted',
      заголовок: 'Чеки ждут приёмки',
      пояснение:
        'Отчёты поданы и ждут решения финотдела. Пока не принято — деньги числятся за человеком.',
      сколько: заявки.items.length,
      единица: ['заявка', 'заявки', 'заявок'],
      сумма: заявки.items.reduce((итог, заявка) => итог + заявка.amount, 0),
      адрес: '/money/claims',
      вес: 100,
    });
  }

  // ── Не отчитались дольше порога ──────────────────────────────────────────────────────
  const зависшие = (лица?.items ?? []).filter(
    (лицо) =>
      лицо.oldest_unreported_days !== null &&
      лицо.oldest_unreported_days >= ПОРОГ_ЗАВИСАНИЯ_ДНЕЙ &&
      лицо.balance.unconfirmed_balance > 0,
  );
  if (зависшие.length > 0) {
    const дольше_всех = зависшие.reduce((худший, лицо) =>
      (лицо.oldest_unreported_days ?? 0) > (худший.oldest_unreported_days ?? 0) ? лицо : худший,
    );
    дела.push({
      код: 'unreported_overdue',
      заголовок: `Не отчитались дольше ${ДНИ(ПОРОГ_ЗАВИСАНИЯ_ДНЕЙ)}`,
      // «23 дня», а не «23 дней»: несогласованное число читается как поломка, и человек
      // перестаёт доверять остальному тексту — включая суммы.
      пояснение: `Дольше всех — ${дольше_всех.employee_name}, ${ДНИ(дольше_всех.oldest_unreported_days ?? 0)}. Об этих деньгах со временем забывают, и они превращаются в невозвратные потери.`,
      сколько: зависшие.length,
      единица: ['человек', 'человека', 'человек'],
      сумма: зависшие.reduce((итог, лицо) => итог + лицо.balance.unconfirmed_balance, 0),
      адрес: '/money/accountable',
      вес: 200,
    });
  }

  // ── Уволены, а долг остался ──────────────────────────────────────────────────────────
  // Удерживать такой долг больше не из чего: расчётов у человека не будет. Значит это не
  // напоминание, а развилка — взыскать или признать невозвратной потерей, и решает её
  // человек. Пока решения нет, долг тихо висит в отчётах «действующим».
  if (уволенные !== null && уволенные.items.length > 0) {
    дела.push({
      код: 'debt_after_dismissal',
      заголовок: 'Уволены, а долг остался',
      пояснение:
        'Удержать больше не из чего: расчётов у этих людей не будет. Каждый долг придётся либо взыскивать, либо признавать невозвратной потерей.',
      сколько: уволенные.items.length,
      единица: ['человек', 'человека', 'человек'],
      // Суммы долга в строке реестра нет — она живёт в карточке человека. Показывать
      // «0 ₽» здесь значило бы соврать, поэтому у дела нет денежного веса, а не нулевой.
      сумма: null,
      адрес: '/people/employees?f.status=fired&f.debt=true',
      вес: 180,
    });
  }

  // ── Официальный контур: непроведённые транши и неперенесённый остаток ────────────────
  // Предпросмотр закрытия зависит от периода, поэтому идёт вторым шагом, а не в общей пачке.
  const открытый = периоды?.items.find((период) => период.status === 'open');
  if (открытый !== undefined) {
    const предпросмотр = await fetchClosePreview(открытый.id);
    if (предпросмотр.unpaid_tranches > 0) {
      дела.push({
        код: 'official_unpaid',
        заголовок: 'Непроведённые транши',
        пояснение: `Период ${открытый.title}: выплаты запланированы, но не отмечены проведёнными. Пока не отмечены — остаток человека считается непогашенным.`,
        сколько: предпросмотр.unpaid_tranches,
        единица: ['транш', 'транша', 'траншей'],
        сумма: предпросмотр.unpaid_total,
        адрес: '/payroll/official',
        вес: 150,
      });
    }
    if (предпросмотр.carry_rows > 0) {
      дела.push({
        код: 'official_carry',
        заголовок: 'Остаток перейдёт в следующий период',
        пояснение: `Период ${открытый.title}: остаток не погашен, и при закрытии он перейдёт отдельной строкой — или потребует отказа с основанием.`,
        сколько: предпросмотр.carry_rows,
        единица: ['человек', 'человека', 'человек'],
        сумма: предпросмотр.carry_total,
        адрес: '/payroll/official',
        вес: 120,
      });
    }
  }

  дела.sort((а, б) => б.вес - а.вес);
  return { дела, generated_at: new Date().toISOString(), безПостаПроверен: безПоста !== null };
}

export const casesKeys = {
  все: ['cases'] as const,
  /** Ключ несёт состав источников: у разных ролей очередь разная, и общий кэш их перепутал бы. */
  поИсточникам: (источники: ИсточникиДел) => ['cases', источники] as const,
};

export function useCases(): UseQueryResult<ДелаСводка> {
  const permissions = useAuthStore((state) => state.permissions);
  const источники = источникиПоПравам(permissions);
  return useQuery({
    queryKey: casesKeys.поИсточникам(источники),
    queryFn: () => собрать(источники),
    // Дела — очередь, а не отчёт: их смотрят, когда садятся за работу, и ждут свежести.
    // Минута выбрана так же, как у ленты уведомлений: человек не заметит, что это не мгновенно.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}
